/**
 * CustomerSites — read-only list of the customer's own sites with the
 * counts that matter to them: how many assets we track, how many
 * active work orders we have on-site, and where the site is.
 *
 * All three source endpoints (`/sites`, `/assets`, `/work-orders`)
 * already filter server-side to the customer's org, so nothing here
 * needs bespoke security.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Building2, MapPin, Boxes, ClipboardList } from 'lucide-react';
import { PageHeader } from '../../components/Shared';
import { getSites, getAssets, getWorkOrders } from '../../services/api';
import { useSortableList } from '../../hooks/useSortableList';
import { SortSelect } from '../../components/SortHeader';

const CustomerSites = () => {
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getSites().catch(() => []),
      getAssets().catch(() => []),
      getWorkOrders().catch(() => []),
    ]).then(([s, a, o]) => {
      setSites(s); setAssets(a); setOrders(o);
    }).finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const map = {};
    sites.forEach(s => { map[s.id] = { assets: 0, activeOrders: 0 }; });
    assets.forEach(a => { if (map[a.siteId]) map[a.siteId].assets += 1; });
    orders.forEach(o => {
      if (o.status !== 'completed' && map[o.siteId]) {
        map[o.siteId].activeOrders += 1;
      }
    });
    return map;
  }, [sites, assets, orders]);

  const enrichedSites = useMemo(
    () => sites.map(s => ({
      ...s,
      _assetsCount: counts[s.id]?.assets || 0,
      _activeCount: counts[s.id]?.activeOrders || 0,
    })),
    [sites, counts],
  );

  const { sorted, sort, onSort } = useSortableList(enrichedSites, {
    name:   (s) => (s.name || '').toLowerCase(),
    assets: (s) => s._assetsCount,
    active: (s) => s._activeCount,
  }, {
    initialKey: 'name',
    initialDir: 'asc',
    defaultDescKeys: ['assets', 'active'],
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customer-sites-page">
      <PageHeader
        title="Your sites"
        description="The locations we service for you, with a quick view of the assets and jobs at each."
      />

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : sites.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500 text-sm">
          <Building2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          No sites configured yet. Contact Lutrom Engineering to add one.
        </div>
      ) : (
        <>
          <div className="flex justify-end">
            <SortSelect
              sort={sort}
              onSort={onSort}
              options={[
                { key: 'name',   label: 'Name' },
                { key: 'assets', label: 'Number of assets' },
                { key: 'active', label: 'Active work orders' },
              ]}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map(s => {
              return (
                <div
                  key={s.id}
                  className="bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm transition-shadow"
                  data-testid={`site-card-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                    <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                  </div>
                  {s.address && (
                    <div className="text-xs text-slate-500 flex items-start gap-1 mb-3">
                      <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{s.address}</span>
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-xs text-slate-600">
                      <Boxes className="h-3.5 w-3.5 text-slate-500" />
                      <span className="font-semibold text-slate-900">{s._assetsCount}</span> asset{s._assetsCount === 1 ? '' : 's'}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-600">
                      <ClipboardList className="h-3.5 w-3.5 text-slate-500" />
                      <span className="font-semibold text-slate-900">{s._activeCount}</span> active
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerSites;
